import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { Breadcrumbs, EmptyState, Loader } from "@threadws/jute";
import {
  personasGetOptions,
  personasGetQueryKey,
  personasListQueryKey,
  personasUpdateMutation,
} from "../../api/generated/@tanstack/react-query.gen";
import { ConnectionNotice } from "./connections/ConnectionNotice";
import { PersonaConnections } from "./connections/PersonaConnections";
import { PersonaForm, type PersonaFormValues } from "./PersonaForm";
import { PersonaPeople } from "./PersonaPeople";

export function EditPersonaPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { data: persona, isPending, error } = useQuery(personasGetOptions({ path: { id } }));
  const update = useMutation(personasUpdateMutation());
  const toList = () => navigate("/personas");

  if (isPending) return <Loader label="Loading persona" />;
  // A malformed id fails validation (400), so treat it like a missing persona.
  const missing = error?.statusCode === 400 || error?.statusCode === 404;
  // A failed background refetch keeps the cached persona, so keep the form (and its edits) mounted.
  if (error && (missing || !persona)) {
    return (
      <EmptyState
        title={missing ? "Persona not found" : "Couldn't load persona"}
        description={missing ? "It may have been deleted." : error.message}
        actionLabel="Back to personas"
        onAction={toList}
      />
    );
  }

  const save = async ({ name, description, rules }: PersonaFormValues) => {
    const saved = await update.mutateAsync({ path: { id }, body: { name, description, rules } });
    queryClient.setQueryData(personasGetQueryKey({ path: { id } }), saved);
    await queryClient.invalidateQueries({ queryKey: personasListQueryKey() });
    toList();
  };

  return (
    <section className="flex w-full max-w-3xl flex-col gap-6 px-4">
      <Breadcrumbs items={[{ label: "Personas", onClick: toList }, { label: persona.name }]} />
      <ConnectionNotice />
      <h1 className="text-2xl font-medium">Edit persona</h1>
      <PersonaForm
        key={persona.id}
        values={{ name: persona.name, description: persona.description, rules: persona.rules }}
        onSubmit={save}
        onCancel={toList}
        submitLabel="Save"
      />
      <PersonaPeople personaId={persona.id} />
      <PersonaConnections personaId={persona.id} />
    </section>
  );
}
